import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Spin, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useAllAccountBalances } from '@/hooks/accounts/useCustomerAccount';
import { format } from '@/components/common/utils/money';
import { useAuthUser } from '@/modules/auth/context/AuthContext';
import type { TransactionDTO } from '@/shared/api/model';

const { Title, Text } = Typography;

interface AccountBalanceRow {
  key: string;
  productName: string;
  accountNo: string;
  glNo: string;
  balance: number;
  sessionDate?: string;
}

const AccountBalances: React.FC = () => {
  const authUser = useAuthUser();
  const branch = authUser?.branch || '';
  const custNo = authUser?.custNo || '';
  const [isBalanceVisible, setIsBalanceVisible] = useState<boolean>(false);

  const [balancesQuery] = useAllAccountBalances(branch, custNo);

  const rows = useMemo<AccountBalanceRow[]>(() => {
    const balances = balancesQuery.data || [];

    return balances.map((item: TransactionDTO, index: number) => ({
      key: `${item.glNo || 'gl'}-${item.acNo || 'ac'}-${index}`,
      productName: item.productName || item.ledgerName || '-',
      accountNo: item.acNo || '-',
      glNo: item.glNo || '-',
      balance: item.amount || 0,
      sessionDate: item.sessionDate,
    }));
  }, [balancesQuery.data]);

  const asOfDate = rows[0]?.sessionDate
    ? new Date(rows[0].sessionDate).toLocaleDateString()
    : '-';

  const columns: ColumnsType<AccountBalanceRow> = [
    {
      title: 'Product',
      dataIndex: 'productName',
      key: 'productName',
    },
    {
      title: 'Account No',
      dataIndex: 'accountNo',
      key: 'accountNo',
    },
    {
      title: 'GL No',
      dataIndex: 'glNo',
      key: 'glNo',
    },
    {
      title: 'Current Balance',
      dataIndex: 'balance',
      key: 'balance',
      align: 'right',
      render: (value: number) => <Text strong>{format(value)}</Text>,
    },
    {
      title: 'Session Date',
      dataIndex: 'sessionDate',
      key: 'sessionDate',
      render: (value?: string) => (value ? new Date(value).toLocaleDateString() : '-'),
    },
  ];

  return (
    <Card bordered={false} className="header-solid h-full">
      <div className="mb-4 flex items-start justify-between gap-4">
        <Title level={4} style={{ marginBottom: 0 }}>
          My Account Balances
        </Title>
        <Button
          type={isBalanceVisible ? 'default' : 'primary'}
          onClick={() => setIsBalanceVisible(!isBalanceVisible)}
          icon={isBalanceVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
        >
          {isBalanceVisible ? 'Hide Balances' : 'Show Balances'}
        </Button>
      </div>

      <Text type="secondary">As of session date: {asOfDate}</Text>

      {balancesQuery.isLoading ? (
        <div className="py-8 text-center">
          <Spin size="large" />
        </div>
      ) : balancesQuery.isError ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load account balances"
          description="Please try again after a short while."
        />
      ) : !isBalanceVisible ? (
        <Alert
          type="info"
          showIcon
          className="mt-4"
          message="Balances are hidden"
          description="Click 'Show Balances' to reveal your account balances."
        />
      ) : (
        <Table
          rowKey="key"
          columns={columns}
          dataSource={rows}
          pagination={false}
          locale={{ emptyText: 'No balances found for your accounts.' }}
          scroll={{ x: 700 }}
        />
      )}
    </Card>
  );
};

export default AccountBalances;
